import { UseQueryOptions, UseQueryResult, useQuery } from '@tanstack/react-query';

import { useApplicationContext } from 'shared/context/ApplicationContext';
import { useClientSideState } from 'shared/context/ServerSideStoreContext';
import type { IPublicDesktopV2OffersGetOffersResponse } from 'shared/repositories/commercial-search-offers/desktop/v2/offers/get-offers';
import { completeJsonQuerySelector } from 'shared/store/clientSide/slices/jsonQuery/selectors/smart';
import { getCancellationTokenFromSignal } from 'shared/utils/getCancellationTokenFromSignal';

import { QueryError, QueryKey, getQueryKey } from './internal/config';
import { fetchGetOffersService } from './internal/fetchService';

type UseGetOffersQueryOptions<TData> = UseQueryOptions<
  IPublicDesktopV2OffersGetOffersResponse,
  QueryError,
  TData,
  QueryKey
>;

export function useGetOffersQuery<TData = IPublicDesktopV2OffersGetOffersResponse>(
  options?: UseGetOffersQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const applicationContext = useApplicationContext();
  const clientSideState = useClientSideState();

  return useQuery<IPublicDesktopV2OffersGetOffersResponse, QueryError, TData, QueryKey>({
    keepPreviousData: true,
    queryFn: ({ signal }) => {
      const jsonQuery = completeJsonQuerySelector(clientSideState.getState());

      return fetchGetOffersService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters: { jsonQuery },
      });
    },
    queryKey: getQueryKey(),
    ...options,
  });
}
